// lyrics-sync.js

/**
 * Initialize synced lyrics highlighting + screen-reader announcements.
 *
 * @param {Object} options
 * @param {HTMLAudioElement} options.audioElement - The real audio element used by the player.
 * @param {HTMLElement} [options.container] - The lyrics container; if omitted we query for it.
 *
 * Expects markup like:
 * <section class="lyrics-section">
 *   <div class="lyrics-static" data-lyrics-mode="synced">
 *     <p class="lyric-line" data-time="1.234">First line</p>
 *     ...
 *   </div>
 * </section>
 *
 * And an optional live region:
 * <div id="lyricsLiveRegion" class="sr-only" aria-live="polite" aria-atomic="true"></div>
 */
export function initLyricsSync({ audioElement, container } = {}) {
  // Basic guard: audio element must look like a real media element
  if (!audioElement || typeof audioElement.addEventListener !== 'function') {
    console.warn('[Lyrics] initLyricsSync: invalid or missing audioElement; skipping.');
    return null;
  }

  // If no container was explicitly passed, try to find the default one.
  const targetContainer =
    container ||
    document.querySelector('.lyrics-section .lyrics-static[data-lyrics-mode="synced"]');

  if (!targetContainer) {
    console.warn('[Lyrics] initLyricsSync: no synced lyrics container found; skipping.');
    return null;
  }

  const lineEls = Array.from(targetContainer.querySelectorAll('.lyric-line'));
  if (!lineEls.length) {
    console.warn('[Lyrics] initLyricsSync: no .lyric-line elements found; skipping.');
    return null;
  }

  // Build a sorted list of lines with timestamps + text
  const lines = lineEls
    .map((el, index) => {
      const t = parseFloat(el.dataset.time || '0');
      return {
        el,
        index,
        time: Number.isFinite(t) ? t : 0,
        text: el.textContent.trim(),
      };
    })
    .sort((a, b) => a.time - b.time);

  const liveRegion = document.getElementById('lyricsLiveRegion');
  let currentIndex = -1;

  function announce(text) {
    if (!liveRegion) return;
    liveRegion.textContent = text ? `Lyric: ${text}` : '';
  }

  function findActiveIndex(time) {
    let active = -1;
    for (let i = 0; i < lines.length; i++) {
      if (time >= lines[i].time) {
        active = i;
      } else {
        break;
      }
    }
    return active;
  }

  function updateActiveLine(time) {
    const newIndex = findActiveIndex(time);
    if (newIndex === currentIndex) return;
    currentIndex = newIndex;

    // Visual highlight
    lineEls.forEach((el, i) => {
      el.classList.toggle('active', i === currentIndex);
    });

    // Screen-reader announcement
    if (currentIndex >= 0) {
      announce(lines[currentIndex].text);
    } else {
      announce('');
    }
  }

  function onTimeUpdate() {
    const t = audioElement.currentTime || 0;
    updateActiveLine(t);
  }

  function onEnded() {
    // Clear highlight and announce end of song
    currentIndex = -1;
    lineEls.forEach((el) => el.classList.remove('active'));
    announce('Song ended');
  }

  audioElement.addEventListener('timeupdate', onTimeUpdate);
  audioElement.addEventListener('ended', onEnded);

  return {
    destroy() {
      audioElement.removeEventListener('timeupdate', onTimeUpdate);
      audioElement.removeEventListener('ended', onEnded);
    },
  };
}
